import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DropDown from '../../../../../components/DropDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList, faPlus, faCaretUp, faCaretDown, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faSquarePlus, faSquareMinus } from '@fortawesome/pro-solid-svg-icons';
import FilterCriteria from '../components/filterCriteria/filterCriteria';
import PropertiesModal from '../../../../assignments/components/PropertiesModal';
import { useGetAssignmentPropertiesMutation, useGetAllUserAssignmentsMutation } from '../../../../../features/authentication/assignmentApislice';
import { useOutletContext, useParams } from 'react-router';
import CustomToast from '../../../../../components/Toast';
import { ResourceUtils } from '../../../../assignments/components/questionDetailComponents/ResourceUtils';
import { DateFormatter } from '../../../../../utils/DateFormatter';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import QuestionTypes from '../../../../../enums/questionTypes';
import TableSkeleton from '../../../../../components/SkeletonLoader/TableSkeleton';
import { useGetYourAssignmentMutation, useUpdateYourAssignmentMutation } from '../../../../../features/authentication/persistApiSlice';
import InfiniteScroll from '../../../../../components/InfiniteScroll/InfiniteScroll';

const YourAssignments = ({ courses, folders, setSelectedAssignmentId }) => {
  const [selectedCourseId, setSelectedCourseId] = useState(-1);
  const [selectedFolderId, setSelectedFolderId] = useState(-1);
  const [openFilterCriteria, setOpenFilterCriteria] = useState(false);
  const [userAssignments, setUserAssignments] = useState([]);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(true);
  const [mCValue, setMCValue] = useState(0);
  const [fIValue, setFIValue] = useState(0);
  const [cRValue, setCRValue] = useState(0);
  const [openAssignmentProperties, setOpenAssignmentProperties] = useState(false);
  const [isLoadingAssignmentProperties, setIsLoadingAssignmentProperties] = useState(false);
  const [assignmentProperties, setAssignmentProperties] = useState([]);
  const [selectedAssignmentName, setSelectedAssignmentName] = useState('');
  const [sortId, setSortId] = useState('assignmentName');
  const [isAscending, setIsAscending] = useState(true);
  const [openYourAssignmentsList, setOpenYourAssignmentsList] = useState(false);
  const [noOfAssignments, setNoOfAssignments] = useState(0);
  const [isAtTop, setIsAtTop] = useState(false);
  const [nextCall, setNextCall] = useState(false);
  const [skipValue, setSkipValue] = useState(0);
  const [selectedPropertiesAssignmentId, setSelectedPropertiesAssignmentId] = useState(0);
  const [persistLoad, setPersistLoad] = useState(false);
  const [scrollAssignmentsList, setScrollAssignmentsList] = useState([]);

  const limit = 30;
  const { assignmentId } = useParams();
  const outletContext = useOutletContext();

  const [getUserAssignments] = useGetAllUserAssignmentsMutation();
  const [getAssignmentProperties] = useGetAssignmentPropertiesMutation();
  const [getPersist] = useGetYourAssignmentMutation();
  const [updatePersist] = useUpdateYourAssignmentMutation();

  useEffect(() => {
    setOpenYourAssignmentsList(true);
    handleGetPersist();
  }, []);

  useEffect(() => {
    if(persistLoad) {
      setSelectedAssignmentId(0);
      outletContext.endCastleSearch();
      setOpenYourAssignmentsList(true);
      setScrollAssignmentsList([]);
      setIsLoadingAssignments(true);
      getUserAssignmentsAsync(0, limit);
      handleUpdatePersist();

      setIsAtTop(false);
      setNextCall(false);
      setSkipValue(0);
    }
  }, [selectedCourseId, selectedFolderId, mCValue, fIValue, cRValue, persistLoad]);
  
  const handleGetPersist = async() => {
    var response = await getPersist().unwrap();

    setSelectedCourseId(response.courseId);
    setSelectedFolderId(response.folderId);
    setMCValue(response.qTypes?.find(x => x.qType === QuestionTypes.MC)?.value);
    setFIValue(response.qTypes?.find(x => x.qType === QuestionTypes.FillIn)?.value);
    setCRValue(response.qTypes?.find(x => x.qType === QuestionTypes.CR)?.value);

    setPersistLoad(true);
  };

  const handleUpdatePersist = async() => {
    const options = {
      courseId: selectedCourseId,
      folderId: selectedFolderId,
      qTypes: [
        {
          'qType': QuestionTypes.MC,
          'value': mCValue
        },
        {
          'qType': QuestionTypes.FillIn,
          'value': fIValue
        },
        {
          'qType': QuestionTypes.CR,
          'value': cRValue
        }
      ]
    };

    await updatePersist(options);
  };

  const getUserAssignmentsAsync = async (skip, take, isScrolling) => {
    const options = {
      courseId: selectedCourseId,
      folderId: selectedFolderId,
      qTypes: [
        {
          'qType': QuestionTypes.MC,
          'value': mCValue
        },
        {
          'qType': QuestionTypes.FillIn,
          'value': fIValue
        },
        {
          'qType': QuestionTypes.CR,
          'value': cRValue
        }
      ],
      skip: skip,
      take: take
    };

    try {
      var response = await getUserAssignments(options);
      if (response.error) {
        CustomToast(response.error.data.error, 'error');
      }
      else {
        if (assignmentId)
        {
          var assignmentList = response.data.data.filter(x => x.assignmentId != assignmentId);
          response = { ...response, data: { ...response.data, data: assignmentList } };
        }

        let updatedAssignmentList = response.data.data;
        setScrollAssignmentsList(updatedAssignmentList);
        if(response.data.data.length < limit && isScrolling)
        {
          updatedAssignmentList = [...scrollAssignmentsList, ...updatedAssignmentList];
          updatedAssignmentList = updatedAssignmentList.slice(response.data.data.length);
        }
        setUserAssignments(updatedAssignmentList);
        setNoOfAssignments(response.data.total);
        setIsLoadingAssignments(false);
      }
    } catch (error) {
      console.log(error);
    }
    setOpenYourAssignmentsList(true);
  };

  const getAssignmentPropertiesAsync = async (assignmentId) => {
    if (!isLoadingAssignmentProperties) {
      setIsLoadingAssignmentProperties(true);
      try {
        const option = {
          assignmentId: assignmentId
        };
        const assignmentsProperties = await getAssignmentProperties(option);
        if (assignmentsProperties.error) {
          CustomToast(assignmentsProperties.error.data.error, 'error');
        }
        else {
          setAssignmentProperties(assignmentsProperties.data);
        }
        setIsLoadingAssignmentProperties(false);
      } catch (error) {
        setIsLoadingAssignmentProperties(false);
        console.log(error);
      }
    }
  };

  const sortButtons = (currentSortId) => {
    return <button className="assignment-sort-btn sort-btn row p-1 mt-2">
      <FontAwesomeIcon icon={faCaretUp} className={`${sortId === currentSortId && isAscending ? 'enabled-caret' : ''}`} />
      <FontAwesomeIcon icon={faCaretDown} className={`align-caret-down ${sortId === currentSortId && !isAscending ? 'enabled-caret' : ''}`} />
    </button>;
  };

  const handleSort = async (currentSortId) => {
    let sortAsc = true;
    if (currentSortId === sortId)
      sortAsc = !isAscending;
    setSortId(currentSortId);

    let sortedAssignments = await ResourceUtils.customSort(userAssignments, currentSortId, sortAsc);
    setUserAssignments(sortedAssignments);
    setIsAscending(sortAsc);
  };

  const handleColSpan = () => {
    let colSpan = 8;
    if(selectedCourseId != -1)
      colSpan = colSpan-1;
    if(selectedFolderId != -1)
      colSpan = colSpan-1;
    if(mCValue == -1)
      colSpan = colSpan-1;
    if(fIValue == -1)
      colSpan = colSpan-1;
    if(cRValue == -1)
      colSpan = colSpan-1;

    return colSpan;
  };

  return (
    <>
      <div className='d-flex flex-row gap-4 mb-3'>
        <div className='d-flex gap-3 w-100 flex-row align-items-center justify-content-between'>
          <div className='d-flex gap-3 w-50'>
            <div className='d-flex flex-column w-50'>
              <DropDown
                key='dropDownCourse'
                label="Course"
                labelClassName="fw-500 mb-0 pb-1"
                value={courses}
                onChangeHandler={(e) => setSelectedCourseId(parseInt(e.target.value))}
                selectedValue={selectedCourseId}
              />
            </div>
            <div className='d-flex flex-column w-50'>
              <DropDown
                key='dropDownFolder'
                label="Assignment Folder"
                labelClassName="fw-500 mb-0 pb-1"
                value={folders}
                onChangeHandler={(e) => setSelectedFolderId(parseInt(e.target.value))}
                selectedValue={selectedFolderId}
              />
            </div>
          </div>
          <div className='d-flex align-items-center align-self-end fw-500 text-castleOrange cursor-pointer'
            onClick={() => setOpenFilterCriteria(!openFilterCriteria)}>
            <span>Filter Criteria</span>
            <FontAwesomeIcon className='fs-6 ms-2' icon={openFilterCriteria ? faSquareMinus : faSquarePlus} />
          </div>
        </div>

      </div>
      {openFilterCriteria &&
        <FilterCriteria
          setMCValue={setMCValue}
          mCValue={mCValue}
          setFIValue={setFIValue}
          fIValue={fIValue}
          setCRValue={setCRValue}
          cRValue={cRValue}
          displayShowArchived={false}
        />
      }
      {openYourAssignmentsList &&
        <div className='mt-3'>
          <div className='cursor-none pb-3'>
            Click
            <button
              id={'plusBtn'}
              className="btn grid-icon-btn success-icon-btn mx-2">
              <FontAwesomeIcon icon={faPlus} />
            </button>
            icon to add selected questions into your assignment or
            <button
              id={'viewBtn'}
              className="btn grid-icon-btn info-icon-btn mx-2">
              <FontAwesomeIcon icon={faClipboardList} />
            </button>
            icon to view the selected assignment properties.
          </div>
          <InfiniteScroll
            limit={limit}
            totalData={noOfAssignments}
            getData={getUserAssignmentsAsync}
            setIsAtTop={setIsAtTop}
            nextCall={nextCall}
            setNextCall={setNextCall}
            skipValue={skipValue}
            setSkipValue={setSkipValue}>
            <table className='table reportTable'>
              {userAssignments.length > 0 && !isLoadingAssignments ? (
                <>
                  <thead className='thead-light align-middle'>
                    <tr>
                      <th id='assignmentName' scope="col" className='fw-medium min-w-400' onClick={(e) => handleSort(e.currentTarget.id)}>
                        Assignment Name
                        {sortButtons('assignmentName')}
                      </th>
                      {selectedFolderId === -1 &&
                        <th id='folderName' scope="col" className='fw-medium' onClick={(e) => handleSort(e.currentTarget.id)}>
                          Folder
                          {sortButtons('folderName')}
                        </th>}
                      {selectedCourseId === -1 &&
                        <th id='courseName' scope="col" className='fw-medium' onClick={(e) => handleSort(e.currentTarget.id)}>
                          Course
                          {sortButtons('courseName')}
                        </th>}
                      <th id='createdDate' scope="col" className='fw-medium text-center' onClick={(e) => handleSort(e.currentTarget.id)}>
                        Create Date
                        {sortButtons('createdDate')}
                      </th>
                      {mCValue != -1 &&
                        <th id='mcCount' scope="col" className='fw-medium text-center' onClick={(e) => handleSort(e.currentTarget.id)}>
                          MC Count
                          {sortButtons('mcCount')}
                        </th>
                      }
                      {fIValue != -1 &&
                        <th id='fiCount' scope="col" className='fw-medium text-center' onClick={(e) => handleSort(e.currentTarget.id)}>
                          FI Count
                          {sortButtons('fiCount')}
                        </th>
                      }
                      {cRValue != -1 &&
                        <th id='crCount' scope="col" className='fw-medium text-center' onClick={(e) => handleSort(e.currentTarget.id)}>
                          CR Count
                          {sortButtons('crCount')}
                        </th>
                      }
                      <th>
                        {'   '}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isAtTop && nextCall && (
                      <tr>
                        <td colSpan={handleColSpan()}>
                          <div className='scroll-spinner mt-3 d-flex align-item-center justify-content-center'>
                            <FontAwesomeIcon icon={faSpinner} spin /><span className='ms-2'>Load More</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    {userAssignments?.map((item, index) => (
                      <tr key={item.assignmentId}>
                        <td className='w-min-max-250 text-truncate'>{item.assignmentName}</td>
                        {selectedFolderId === -1 && <td>{item.folderName}</td>}
                        {selectedCourseId === -1 && <td>{item.courseName}</td>}
                        <td className='text-center'>
                          {DateFormatter.toShortDate(item.createdDate)}
                        </td>
                        {mCValue != -1 && <td className='text-center'>{item.mcCount}</td>}
                        {fIValue != -1 && <td className='text-center'>{item.fiCount}</td>}
                        {cRValue != -1 && <td className='text-center'>{item.crCount}</td>}
                        <td>
                          <div className='d-flex justify-content-center align-items-center gap-2'>
                            <OverlayTrigger
                              key={`viewPropertiesToolTip${item.assignmentId}`}
                              placement="top"
                              overlay={<Tooltip className="custom-tooltip">View Properties</Tooltip>}>
                              <button
                                id={'viewPropties' + index}
                                className='btn grid-icon-btn info-icon-btn'
                                onClick={() => { getAssignmentPropertiesAsync(item.assignmentId); setSelectedPropertiesAssignmentId(item.assignmentId); setSelectedAssignmentName(item.assignmentName); setOpenAssignmentProperties(true); }}
                              >
                                <FontAwesomeIcon icon={faClipboardList} />
                              </button>
                            </OverlayTrigger>
                            <OverlayTrigger
                              key={`addQuestionToolTip${item.assignmentId}`}
                              placement="top"
                              overlay={<Tooltip className="custom-tooltip">Add Questions to Assignment</Tooltip>}>
                              <button
                                id={'addQuestions' + index}
                                className='btn grid-icon-btn success-icon-btn'
                                onClick={async () => {
                                  setSelectedAssignmentName(item.assignmentName);
                                  await setSelectedAssignmentId(item.assignmentId);
                                  setOpenYourAssignmentsList(false);
                                  setOpenFilterCriteria(false);
                                }}
                              >
                                <FontAwesomeIcon icon={faPlus} />
                              </button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </>
              ) : isLoadingAssignments ? (
                <>
                  <TableSkeleton
                    colCount={5}
                    rowCount={4}
                  />
                </>
              ) :
                <p className='text-danger'>There are no Assignments available.</p>
              }
            </table>
          </InfiniteScroll>
        </div>
      }
      {openAssignmentProperties &&
        <PropertiesModal
          title={selectedAssignmentName}
          properties={assignmentProperties}
          isLoading={isLoadingAssignmentProperties}
          openProperties={openAssignmentProperties}
          setOpenProperties={setOpenAssignmentProperties}
          assignmentId={selectedPropertiesAssignmentId}
          isPublicAssignment={false}
        />
      }
    </>
  );
};

YourAssignments.propTypes = {
  courses: PropTypes.array,
  folders: PropTypes.array,
  setSelectedAssignmentId: PropTypes.func
};

export default YourAssignments;